import { coalesceAsync } from 'promise-coalesce';
import * as rxjs from 'rxjs';
import { v4 as uuid } from 'uuid';

/**
 * Resolves true if the condition returns true before the timeout, else false.
 */
export const waitUntil = async <T>(options: {
  key?: string;
  /**
   * Evaluated each interval until it returns true or the timeout is reached.
   */
  action: () => Promise<T>;
  /**
   * How often to evaluate the condition, in milliseconds.
   */
  interval: number;
  /**
   * How long to wait before stop and return false, in milliseconds.
   */
  timeout: number;
}): Promise<T | undefined> => {
  const { action, interval, timeout } = options;

  const key = options.key ?? uuid();

  return coalesceAsync(key, async () => {
    const poller$ = rxjs.interval(interval).pipe(
      rxjs.mergeMap(async () => {
        try {
          return await action();
        } catch (err) {
          // do nothing
        }
      }, 1),
      rxjs.filter((m) => m !== undefined), // check if condition met yet
      rxjs.take(1), // stop poller after conditions met
      rxjs.timeout(timeout), // abort if conditions not met in time
      rxjs.catchError(() => {
        return rxjs.of(undefined);
      }) // convert timeout error to false
    );

    return rxjs.firstValueFrom(poller$); // resolves once a value is emitted
  });
};
